/** The app's one modal. Extracted from `ui/Confirm.tsx` (2026-09-10) when the live view
 * needed sheets of its own: the ask-aloud composer, the shortcut list, post-hoc scoring.
 *
 * There is exactly one set of dialog mechanics in this app and this is it — portal, scrim,
 * `role="dialog"` with `aria-modal`, the app behind made `inert`, focus moved in on open and
 * returned to the trigger on close, Tab cycling inside, Escape cancelling. `Confirm` renders
 * its question inside this; nothing else may write a second modal.
 *
 * Why not a native `<dialog>`: the Teacher Computer app is a Tauri shell whose webview (wry)
 * has been unreliable about the native dialog behaviours (see the note in `Confirm.tsx`), so
 * the app draws its own and behaves the same in a browser, in the desktop shell and in the
 * mobile shells.
 */
import { useEffect, useId, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

type Props = {
  /** The sheet's heading; it labels the dialog for assistive technology. */
  title: string;
  /** One line under the title, and the dialog's description. */
  lead?: string;
  onClose: () => void;
  /** Which control focus starts on. The default is the first focusable thing. */
  initialFocus?: (box: HTMLElement) => HTMLElement | null | undefined;
  /** Rendered after the title; the sheet supplies no buttons of its own. */
  children: ReactNode;
  /** Drawn to the right of the title, e.g. a close button. */
  aside?: ReactNode;
};

export function Sheet({ title, lead, onClose, initialFocus, children, aside }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const leadId = useId();

  useEffect(() => {
    const root = document.getElementById("root");
    const returnTo = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    root?.setAttribute("inert", "");
    document.body.style.overflow = "hidden";
    const first = box.current
      ? (initialFocus?.(box.current) ?? box.current.querySelector<HTMLElement>(FOCUSABLE))
      : null;
    first?.focus();
    return () => {
      root?.removeAttribute("inert");
      document.body.style.overflow = overflow;
      if (returnTo?.isConnected) returnTo.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    // The live view's global key layer stands down inside a dialog (see live/shortcuts.ts),
    // so nothing here needs to guard against a stray letter asking a question.
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key !== "Tab" || !box.current) return;
    const list = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (list.length === 0) return;
    const first = list[0]!;
    const last = list[list.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !box.current.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div
      className="dialog-scrim fixed inset-0 z-50 flex items-end justify-center overflow-auto overscroll-contain bg-[color:var(--scrim)] p-4 pb-[max(1rem,var(--sab))] sm:items-center sm:pb-4"
      onKeyDown={onKeyDown}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={lead ? leadId : undefined}
        className="dialog-box my-auto w-full max-w-[32rem] rounded-[var(--radius-sheet)] bg-[color:var(--surface)] p-6 shadow-md"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-2">
            <h2 id={titleId} className="display break-words text-xl">{title}</h2>
            {lead && <p id={leadId} className="measure break-words text-sm text-[color:var(--ink-2)]">{lead}</p>}
          </div>
          {aside}
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
